// components/PlayButton.tsx
import React, { useEffect, useState } from "react";
import { useBLE } from "../../../hooks/useBle";
import playIcon from "../../../assets/icons/play.svg";
import pauseIcon from "../../../assets/icons/pause.svg";
import { SHOW_THERAPY_CONTROLS } from "../../../utils/constants";

interface PlayButtonProps {
  isPlaying: boolean;
  onToggle: () => void;
  isShortHeight: boolean;
  isMobile: boolean;
  isTablet: boolean;
  scale: number;
  onScaleChange: (scale: number) => void;
}

const PlayButton: React.FC<PlayButtonProps> = ({
  isPlaying,
  onToggle,
  isShortHeight,
  isMobile,
  isTablet,
  scale,
  onScaleChange,
}) => {
  const { isConnected } = useBLE();
  
  // Force a re-render after component mounts to ensure media queries are evaluated
  const [mountKey, setMountKey] = useState(0);
  
  useEffect(() => {
    // Force re-render after media queries have had time to evaluate
    const timer = setTimeout(() => {
      setMountKey(prev => prev + 1);
    }, 50); // Short delay to let media queries settle
    
    return () => clearTimeout(timer);
  }, []); // Only run on mount

  const getIconSize = () => {
    // Add explicit check for undefined/false values to ensure proper evaluation
    if (isTablet === true) return "120px";
    if (isShortHeight === true) return "60px";
    if (isMobile === true) return "90px";
    
    // Fallback: detect directly if props seem uninitialized
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1024 && width <= 1400) return "120px"; // iPad range
      if (width < 1024) return "90px";
    }
    
    return "90px"; // Safe default
  };

  const triggerHapticFeedback = () => {
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }
  };

  // Inert without a device, and always on web.
  const isDisabled = !isConnected || !SHOW_THERAPY_CONTROLS;

  const handleClick = () => {
    if (isDisabled) {
      triggerHapticFeedback();
      return;
    }

    onScaleChange(0.9);
    setTimeout(() => {
      onScaleChange(1);
      onToggle();
    }, 100);
  };

  const getButtonStyles = () => {
    const getButtonSize = () => {
      // Explicit boolean checks to avoid falsy evaluation issues
      if (isTablet === true) return "120px";
      if (isShortHeight === true) return "70px";
      if (isMobile === true) return "100px";
      
      // Fallback detection
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width >= 1024 && width <= 1400) return "120px";
        if (width < 1024) return "100px";
      }
      
      return "100px"; // Safe default
    };

    const getScale = () => {
      let baseScale = 1;
      if (isShortHeight === true) baseScale = 0.9;
      return baseScale * scale;
    };

    const buttonSize = getButtonSize();

    const baseStyles = {
      transform: `scale(${getScale()})`,
      transition: "all 0.2s ease",
      width: buttonSize,
      height: buttonSize,
    };

    if (!isDisabled) {
      return {
        ...baseStyles,
        opacity: 1,
        cursor: "pointer",
      };
    }
    return {
      ...baseStyles,
      // Web stays visible; greyscale carries the disabled look.
      opacity: SHOW_THERAPY_CONTROLS ? 0.4 : 0.35,
      cursor: "not-allowed",
    };
  };

  const getIconStyles = () => {
    const baseStyles = {
      width: getIconSize(),
      height: getIconSize(),
      transition: "opacity 0.2s ease",
    };

    if (!isDisabled) {
      return {
        ...baseStyles,
        opacity: 1,
      };
    }
    return {
      ...baseStyles,
      // Ghosted on web so the play glyph doesn't stay crisp.
      opacity: SHOW_THERAPY_CONTROLS ? 0.3 : 0.35,
    };
  };

  return (
    <div className="play-button-container" key={mountKey}>
      <button
        className={`play-button-enhanced rounded-full flex items-center justify-center ${
          isDisabled ? "play-button-disabled" : ""
        }`}
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={
          !SHOW_THERAPY_CONTROLS
            ? "Therapy control is not available in the browser"
            : !isConnected
            ? "Connect device to start therapy"
            : isPlaying
            ? "Pause therapy"
            : "Start therapy"
        }
        style={getButtonStyles()}
      >
        <div
          className={`rotating-border ${
            isDisabled ? "rotating-border-disabled" : ""
          }`}
          style={{
            // Ring barely there on web.
            opacity: isDisabled ? (SHOW_THERAPY_CONTROLS ? 0.2 : 0.15) : 1,
          }}
        ></div>
        <div className="play-icon-container">
          <img
            src={isPlaying ? pauseIcon : playIcon}
            alt={isPlaying ? "Pause" : "Play"}
            style={getIconStyles()}
          />
        </div>
      </button>

      <style>{`
        .play-button-disabled {
          filter: grayscale(${SHOW_THERAPY_CONTROLS ? "20%" : "100%"});
        }
        
        .rotating-border-disabled {
          animation-play-state: paused !important;
        }
        
        .rotating-border:not(.rotating-border-disabled) {
          animation-play-state: running;
        }
      `}</style>
    </div>
  );
};

export default PlayButton;